import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, BookOpen, ArrowRight, Search, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';

const QUICK_REF = [
  { title: 'Create Enquiry', desc: 'Sidebar → "+ New Enquiry" → follow the wizard steps' },
  { title: 'Run Decision', desc: 'Open enquiry → click "Run Decision" to get Quote/Don\'t Quote' },
  { title: 'Create Quote', desc: 'Enquiry with "Quote" decision → "Create Quote" → add line items' },
  { title: 'Send Quote', desc: 'Approved quote → "Send to Client" → edit message → Send' },
  { title: 'Add Contact', desc: 'Contacts page → "Add Contact" → fill details + Lead Source' },
  { title: 'Enrol in Sequence', desc: 'Open contact → see "Recommended Sequence" → click "Enrol Now"' },
  { title: 'Log Outreach', desc: 'Open contact → "Log Outreach" → select type & outcome' },
  { title: 'Enable Push', desc: 'My Profile → Push Notifications → Enable → grant permission' },
  { title: 'Customise Mobile Nav', desc: 'My Profile → Mobile Navigation → select 4 pages → Save' },
  { title: 'Download PDF', desc: 'Quote or Report page → "Download PDF" button' },
  { title: 'Sync Calendar', desc: 'Calendar → link icon → Subscribe tab → copy URL → paste in calendar app' },
  { title: 'Configure SMTP', desc: 'Admin → System → SMTP Email Configuration → enter host/credentials → Save' },
];

export const HelpPanel = () => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const filtered = search.trim()
    ? QUICK_REF.filter(r =>
        r.title.toLowerCase().includes(search.toLowerCase()) ||
        r.desc.toLowerCase().includes(search.toLowerCase())
      )
    : QUICK_REF;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        className="h-9 w-9 rounded-full hover:bg-white/10"
        data-testid="help-panel-trigger"
        title="Help & Quick Reference"
      >
        <HelpCircle className="h-5 w-5" />
        <span className="sr-only">Help</span>
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md overflow-hidden flex flex-col bg-white"
          data-testid="help-panel"
        >
          <SheetHeader className="pb-4 border-b border-slate-200 pr-8">
            <SheetTitle className="font-heading text-xl text-[#1e3a5f]">
              Quick Reference
            </SheetTitle>
            <SheetDescription className="text-sm text-slate-500">
              How to do common tasks in OpsFlow
            </SheetDescription>
          </SheetHeader>

          {/* Search */}
          <div className="py-3 px-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
              />
            </div>
          </div>

          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-2 pb-4">
              {filtered.map((item, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-xl">
                  <p className="font-medium text-sm text-[#1e3a5f]">{item.title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{item.desc}</p>
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="text-center py-6 text-slate-400 text-sm">
                  No results for "{search}"
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Full Guide Link */}
          <div className="pt-3 border-t border-slate-200">
            <Button
              onClick={() => { setOpen(false); navigate('/help'); }}
              className="w-full bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl"
              data-testid="open-full-guide"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Open Full User Guide
              <ArrowRight className="w-4 h-4 ml-auto" />
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default HelpPanel;
